import { PrivyClient } from "@privy-io/react-auth";
import { getDefaultConfig } from "@rainbow-me/rainbowkit";
import {
  injectedWallet,
  trustWallet,
  walletConnectWallet,
  coinbaseWallet,
  rainbowWallet,
} from "@rainbow-me/rainbowkit/wallets";
import {
  mainnet,
  sepolia,
  arbitrum,
  arbitrumSepolia,
  avalanche,
  avalancheFuji,
  linea,
  lineaTestnet,
} from "wagmi/chains";
import { http } from "wagmi";
import { defineChain } from "viem";

const soneium = defineChain({
  id: 1868,
  name: "Soneium",
  nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
  rpcUrls: { default: { http: ["https://rpc.soneium.org"] } },
  blockExplorers: {
    default: { name: "Soneium Explorer", url: "https://soneium.blockscout.com" },
  },
});

const hemi = defineChain({
  id: 43111,
  name: "Hemi",
  nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
  rpcUrls: { default: { http: ["https://rpc.hemi.network/rpc"] } },
  blockExplorers: {
    default: { name: "Hemi Explorer", url: "https://explorer.hemi.xyz" },
  },
});

const qubetics = defineChain({
  id: 9030,
  name: "Qubetics Mainnet",
  nativeCurrency: { name: "TICS", symbol: "TICS", decimals: 18 },
  rpcUrls: { default: { http: ["https://evm-rpc-arch.qubetics.com/"] } },
  blockExplorers: {
    default: { name: "Qubetics Explorer", url: "https://ticsscan.com" },
  },
});

const privyAppId = import.meta.env.VITE_PRIVY_APP_ID || "";
const walletConnectProjectId =
  import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || "default-project-id";

if (!privyAppId) {
  console.warn(
    "⚠️ VITE_PRIVY_APP_ID is optional. Rainbow Kit will be used as primary wallet connector.",
  );
}

if (walletConnectProjectId === "default-project-id") {
  console.warn(
    "⚠️ VITE_WALLETCONNECT_PROJECT_ID is not set. WalletConnect may not work properly. Get one from https://cloud.walletconnect.com/",
  );
}

export const privyClient = new PrivyClient({
  appId: privyAppId,
});

const chains = [
  mainnet,
  sepolia,
  arbitrum,
  arbitrumSepolia,
  avalanche,
  avalancheFuji,
  linea,
  lineaTestnet,
  soneium,
  hemi,
  qubetics,
] as const;

// Use getDefaultConfig with a custom wallets array to prioritize the injected wallet
export const rainbowConfig = getDefaultConfig({
  appName: "Blazpay Rewards Hub",
  projectId: walletConnectProjectId,
  chains,
  transports: {
    // Use a CORS-friendly mainnet RPC to avoid eth.merkle.io CORS errors on localhost
    [mainnet.id]: http("https://cloudflare-eth.com"),
    [soneium.id]: http("https://rpc.soneium.org"),
    [hemi.id]: http("https://rpc.hemi.network/rpc"),
    [qubetics.id]: http("https://evm-rpc-arch.qubetics.com/"),
    [avalanche.id]: http("https://avalanche.drpc.org/"),
  },
  // NOTE: metaMaskWallet is intentionally omitted. In RainbowKit v2 that
  // connector is backed by the MetaMask SDK, whose connect() frequently hangs
  // on desktop ("Opening MetaMask… Confirm connection in the extension" with no
  // popup). The MetaMask extension is instead surfaced via wagmi's EIP-6963
  // auto-discovery ("Installed" group), which uses the plain injected provider
  // and opens the extension reliably. injectedWallet is the explicit fallback.
  wallets: [
    {
      groupName: "Recommended",
      wallets: [
        injectedWallet,
        trustWallet,
        coinbaseWallet,
        rainbowWallet,
        walletConnectWallet,
      ],
    },
  ],
});

export const wagmiConfig = rainbowConfig;
export default privyClient;
